import { getDownloadTableData } from '../Download/utils/dataGenerator';
import { downloadExcel } from '@/pages/dashboard/utils/download';
import { UnionMergeStrategy } from '../Download/utils/merger';
import { extractMetricDataKey } from '../AIContent/_utils';
import type { 
  IDownloadHandlerParams, 
  IDownloadColumn, 
  ITableDataItem,
  IMultipleSource 
} from './_type';

/**
 * 构建下载列配置
 * @param columnsInfo - 列信息数组
 * @returns 下载列配置数组
 */
const buildColumns = (columnsInfo: any[] = []): IDownloadColumn[] =>
  columnsInfo.map((c) => ({
    code: c.origin_name,
    name: c.name,
    format: c.column_date_type || '',
  }));

/**
 * 处理下载逻辑（支持单数据源和多数据源）
 * @param params - 下载处理参数
 */
export const handleDownload = async (params: IDownloadHandlerParams): Promise<void> => {
  const {
    formattedData,
    rawData = [],
    filterVal = {},
    fileName,
    setNoticeStatus,
    enableNotification = true,
    multipleSources,  // 新增：多数据源参数
  } = params;
  
  // 多数据源模式（多 sheet 下载）
  if (multipleSources && multipleSources.length > 0) {
    console.log(`[handleDownload] 多数据源模式，共 ${multipleSources.length} 个 sheet`);
    return await handleMultipleSourcesDownload({
      multipleSources,
      fileName,
      setNoticeStatus,
      enableNotification,
    });
  }
  
  // 单数据源模式（原有逻辑保持不变）
  if (!formattedData) {
    console.warn('[handleDownload] formattedData 为空');
    return;
  }

  // 如果启用了通知，设置下载中状态
  if (enableNotification && setNoticeStatus) {
    setNoticeStatus('loading');
  }

  try {
    const xAxis = formattedData.xAxis || [];
    const legends = formattedData.legends || [];
    const dimensionInfo = formattedData.dimension_info || [];
    const metricsInfo = formattedData.metrics_info || [];
    const filterInfo = formattedData.filter_info || [];
    const inputData = rawData.length > 0 ? rawData : (formattedData.data || []);

    // 确定要下载的指标：下载所有指标的全量数据
    const metricsToUse: string[] = [];
    metricsInfo.forEach((metric) => {
      const metricKey = extractMetricDataKey(metric.data_key) || metric.metric_key;
      if (metricKey) {
        metricsToUse.push(metricKey);
      }
    });

    if (metricsToUse.length === 0) {
      console.warn('没有可下载的指标');
      return;
    }

    // 处理筛选器值：使用 filterVal，如果没有则使用空对象（下载全量）
    const filterInfoVal: Record<string, any> = { ...filterVal };

    // 为每个指标生成数据
    const tableDataList = await Promise.all(
      metricsToUse.map(async (metric): Promise<ITableDataItem | null> => {
        try {
          // 查找匹配的指标（支持对象格式的 data_key）
          const metricInfo = metricsInfo.find((m) => {
            const mKey = extractMetricDataKey(m.data_key) || m.metric_key;
            return mKey === metric;
          });
          const metricName = metricInfo?.name || metric;

          const chartComData = getDownloadTableData({
            inputData,
            filterInfoVal,
            xAxis,
            inputMetricsInfo: metricsInfo,
            inputLegends: legends,
            dimensionInfo,
            metric,
          });

          if (!chartComData?.columnsInfo || chartComData.columnsInfo.length === 0) {
            return null;
          }

          return {
            data: chartComData.data || [],
            columnsInfo: chartComData.columnsInfo,
            metricName,
            metricKey: metric,
          };
        } catch (error) {
          console.error(`下载指标 ${metric} 时出错:`, error);
          return null;
        }
      })
    );

    // 过滤掉 null
    const validTableDataList = tableDataList.filter(
      (data): data is ITableDataItem => data !== null
    );

    if (validTableDataList.length === 0) {
      console.warn('没有可下载的数据');
      return;
    }

    // 如果只有一个指标，直接下载
    if (validTableDataList.length === 1) {
      const single = validTableDataList[0];
      await downloadExcel({
        fileName,
        tabs: [
          {
            tabName: 'file',
            dataSource: single.data || [],
            columns: buildColumns(single.columnsInfo),
          },
        ],
      });
      // 如果启用了通知，设置下载成功状态
      if (enableNotification && setNoticeStatus) {
        setNoticeStatus('success');
      }
      return;
    }

    // 多个指标，合并数据
    const mergeStrategy = new UnionMergeStrategy();
    const mergedData = mergeStrategy.merge(validTableDataList, {
      xAxis,
      metricsInfo,
      filterInfo,
    });

    // 执行下载
    if (mergedData) {
      await downloadExcel({
        fileName,
        tabs: [
          {
            tabName: 'file',
            dataSource: mergedData?.data || [],
            columns: buildColumns(mergedData?.columnsInfo || []),
          },
        ],
      });
      // 如果启用了通知，设置下载成功状态
      if (enableNotification && setNoticeStatus) {
        setNoticeStatus('success');
      }
      return;
    }

    // 没有合并结果，尝试只导出表头（从第一个指标的 columnsInfo 获取）
    if (validTableDataList.length > 0) {
      const cols = buildColumns(validTableDataList[0].columnsInfo);
      
      await downloadExcel({
        fileName,
        tabs: [
          {
            tabName: 'file',
            dataSource: [],
            columns: cols,
          },
        ],
      });
      // 如果启用了通知，设置下载成功状态
      if (enableNotification && setNoticeStatus) {
        setNoticeStatus('success');
      }
    }
  } catch (error) {
    console.error('下载数据时出错:', error);
    // 如果启用了通知，设置错误状态
    if (enableNotification && setNoticeStatus) {
      setNoticeStatus('error');
    }
    throw error;
  }
};

/**
 * 处理多数据源下载（多 sheet 模式）
 * @description 将多个数据源合并到一个 Excel 文件的多个 sheet 中
 */
export const handleMultipleSourcesDownload = async (params: {
  multipleSources: IMultipleSource[];
  fileName: string;
  setNoticeStatus?: (status: 'loading' | 'success' | 'error') => void;
  enableNotification: boolean;
}): Promise<void> => {
  const { multipleSources, fileName, setNoticeStatus, enableNotification } = params;
  
  if (enableNotification && setNoticeStatus) {
    setNoticeStatus('loading');
  }
  
  try {
    const tabs: Array<{
      tabName: string;
      dataSource: any[];
      columns: IDownloadColumn[];
    }> = [];
    
    // 用于处理 sheet 名称去重
    const usedSheetNames = new Set<string>();
    
    // 为每个数据源生成一个 tab
    for (let i = 0;i < multipleSources.length;i++) {
      const source = multipleSources[i];
      const { sheetName, formattedData, rawData = [] } = source;

      console.log(`[handleMultipleSourcesDownload] 处理 sheet ${i + 1}/${multipleSources.length}: ${sheetName}`);
      
      const xAxis = formattedData.xAxis || [];
      const legends = formattedData.legends || [];
      const dimensionInfo = formattedData.dimension_info || [];
      const metricsInfo = formattedData.metrics_info || [];
      const filterInfo = formattedData.filter_info || [];
      const inputData = rawData.length > 0 ? rawData : (formattedData.data || []);
      
      // 确定要下载的指标
      const metricsToUse: string[] = [];
      metricsInfo.forEach((metric: any) => {
        const metricKey = extractMetricDataKey(metric.data_key) || metric.metric_key;
        if (metricKey) {
          metricsToUse.push(metricKey);
        }
      });
      
      if (metricsToUse.length === 0) {
        console.warn(`Sheet ${sheetName} 没有可下载的指标，跳过`);
        continue;
      }
      
      // 生成表格数据（复用原有逻辑）
      const tableDataList = await Promise.all(
        metricsToUse.map(async (metric): Promise<ITableDataItem | null> => {
          try {
            const metricInfo = metricsInfo.find((m: any) => {
              const mKey = extractMetricDataKey(m.data_key) || m.metric_key;
              return mKey === metric;
            });
            const metricName = metricInfo?.name || metric;
            
            const chartComData = getDownloadTableData({
              inputData,
              filterInfoVal: {},
              xAxis,
              inputMetricsInfo: metricsInfo,
              inputLegends: legends,
              dimensionInfo,
              metric,
            });
            
            if (!chartComData?.columnsInfo || chartComData.columnsInfo.length === 0) {
              return null;
            }
            
            return {
              data: chartComData.data || [],
              columnsInfo: chartComData.columnsInfo,
              metricName,
              metricKey: metric,
            };
          } catch (error) {
            console.error(`Sheet ${sheetName} 下载指标 ${metric} 时出错:`, error);
            return null;
          }
        })
      );
      
      const validTableDataList = tableDataList.filter(
        (data): data is ITableDataItem => data !== null
      );
      
      if (validTableDataList.length === 0) {
        console.warn(`Sheet ${sheetName} 没有有效数据，跳过`);
        continue;
      }
      
      // 处理 sheet 名称去重
      let finalSheetName = sheetName;
      let counter = 1;
      while (usedSheetNames.has(finalSheetName)) {
        finalSheetName = `${sheetName} (${counter})`;
        counter += 1;
      }
      usedSheetNames.add(finalSheetName);
      
      // 合并数据（复用原有逻辑）
      if (validTableDataList.length === 1) {
        const single = validTableDataList[0];
        tabs.push({
          tabName: finalSheetName,
          dataSource: single.data || [],
          columns: buildColumns(single.columnsInfo),
        });
      } else {
        const mergeStrategy = new UnionMergeStrategy();
        const mergedData = mergeStrategy.merge(validTableDataList, {
          xAxis,
          metricsInfo,
          filterInfo,
        });
        
        if (mergedData) {
          tabs.push({
            tabName: finalSheetName,
            dataSource: mergedData.data || [],
            columns: buildColumns(mergedData.columnsInfo || []),
          });
        } else {
          // 如果合并失败，至少导出表头
          tabs.push({
            tabName: finalSheetName,
            dataSource: [],
            columns: buildColumns(validTableDataList[0].columnsInfo),
          });
        }
      }
    }
    
    if (tabs.length === 0) {
      console.warn('[handleMultipleSourcesDownload] 没有可下载的 sheet');
      if (enableNotification && setNoticeStatus) {
        setNoticeStatus('error');
      }
      return;
    }
    
    console.log(`[handleMultipleSourcesDownload] 准备下载 ${tabs.length} 个 sheet`);
    
    // 调用 downloadExcel，传入多个 tabs
    await downloadExcel({
      fileName,
      tabs,
    });
    
    if (enableNotification && setNoticeStatus) {
      setNoticeStatus('success');
    }
    
    console.log(`[handleMultipleSourcesDownload] 下载成功: ${fileName}`);
  } catch (error) {
    console.error('[handleMultipleSourcesDownload] 多 sheet 下载失败:', error);
    if (enableNotification && setNoticeStatus) {
      setNoticeStatus('error');
    }
    throw error;
  }
};


